import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';
import ScriptToggle from '../components/ScriptToggle';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Grammar = () => {
  const navigate = useNavigate();
  const { scriptMode } = usePreferences();
  const { token } = useAuth();
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGrammar();
  }, []);

  // Mark grammar lesson as read
  const markGrammarRead = async (fiche) => {
    if (!token) return;
    try {
      await axios.post(`${API}/user/content-progress`, {
        content_type: 'grammar_read',
        content_id: fiche.id,
        level: fiche.level || 'beginner'
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.log('Could not save grammar progress');
    }
  };

  const handleSelectFiche = (fiche) => {
    setSelectedFiche(fiche);
    markGrammarRead(fiche);
  };

  const fetchGrammar = async () => {
    try {
      const response = await axios.get(`${API}/grammar`);
      setFiches(response.data);
    } catch (error) {
      console.error('Failed to fetch grammar:', error);
      toast.error('Erreur lors du chargement de la grammaire');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="font-playfair text-3xl font-bold text-accent">
                📚 Grammaire
              </h1>
            </div>
            <ScriptToggle />
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {!selectedFiche ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                Fiches de grammaire simples
              </h2>
              <p className="font-nunito text-lg text-charcoal/70">
                {fiches.length} fiches disponibles
              </p>
            </motion.div>

            {fiches.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-nunito text-xl text-charcoal/60">
                  Aucune fiche de grammaire pour le moment
                </p>
                <p className="font-nunito text-sm text-charcoal/50 mt-2">
                  L'administrateur peut en ajouter via l'interface admin
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {fiches.map((fiche, index) => (
                  <motion.button
                    key={fiche.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleSelectFiche(fiche)}
                    className="tile-button bg-white border-2 border-stone-200 rounded-2xl p-6 hover:border-accent hover:shadow-lg card-hover text-left"
                  >
                    <div className="text-5xl mb-4">{fiche.icon}</div>
                    <h3 className="font-playfair text-xl font-bold text-charcoal mb-2">
                      {fiche.title}
                    </h3>
                    <p className="font-nunito text-sm text-charcoal/70">
                      {fiche.examples?.length || 0} exemples
                    </p>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setSelectedFiche(null)}
              className="tile-button bg-stone-200 text-charcoal font-nunito font-semibold px-4 py-2 rounded-lg mb-4 hover:bg-stone-300"
            >
              ← Retour
            </button>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-6xl">{selectedFiche.icon}</span>
              <div>
                <h2 className="font-playfair text-4xl font-bold text-charcoal">
                  {selectedFiche.title}
                </h2>
              </div>
            </div>

            <div className="bg-accent/10 rounded-2xl p-6 mb-8 border-2 border-accent/20">
              <div className="flex items-start gap-3">
                <div className="bg-accent text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  💡
                </div>
                <div className="flex-1 markdown-content">
                  <p className="font-nunito font-bold text-charcoal mb-2">Règle :</p>
                  <div className="font-nunito text-base text-charcoal prose prose-sm max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {selectedFiche.explanation}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="font-playfair text-2xl font-bold text-charcoal mb-6">Exemples :</h3>

            <div className="space-y-4">
              {selectedFiche.examples?.map((example, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border-2 border-stone-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold text-accent">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      {scriptMode === 'arabic' ? (
                        <p className="font-amiri text-3xl text-primary mb-3 text-right">
                          {example.darija_arabic}
                        </p>
                      ) : (
                        <p className="font-nunito text-2xl font-bold text-primary mb-3">
                          {example.darija_phonetic}
                        </p>
                      )}
                      <p className="font-nunito text-lg text-charcoal">🇫🇷 {example.french}</p>
                      <p className="font-nunito text-lg text-charcoal">🇬🇧 {example.english}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Grammar;
